import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as LinkIcon, Plus, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { LINK_KINDS, MANUAL_LINK_TYPES, type LinkType } from '@esa/shared';
import { ApiError, api, type TicketLinkRow, type TicketState } from '../lib/api.js';

/** One row of the picker: enough to be sure it is the right ticket. */
export interface LookupHit {
  number: number;
  subject: string;
  state: TicketState;
  assigneeName: string | null;
}
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  Input,
  Select,
  StateBadge,
  useToast,
} from '../ui/index.js';

/**
 * What this ticket is bound to.
 *
 * THE CASE IT EXISTS FOR: an onboarding request becomes an account ticket and
 * a hardware ticket, worked by different people at different speeds. Each of
 * the three needs to be able to see the other two, because the failure this
 * prevents is quiet — the laptop arrives, that ticket is resolved, and nobody
 * notices the account half is still open.
 *
 * GROUPED BY WHAT THE RELATIONSHIP IS, not listed flat. "Child of" and
 * "Parent of" are different questions — one says what this is part of, the
 * other what it contains — and a flat list makes the reader work out which is
 * which from the direction of an arrow.
 *
 * Every row carries the other ticket's STATE. A list of numbers answers "what
 * is this connected to"; the states answer "can I close this yet", which is
 * the question somebody actually has when they look.
 */
export function TicketLinks({
  tenant,
  number,
  links,
}: {
  tenant: string;
  number: string;
  links: TicketLinkRow[];
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [picking, setPicking] = useState(false);
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['ticket', tenant, number] });
    void qc.invalidateQueries({ queryKey: ['tickets'] });
  };

  const unlink = useMutation({
    mutationFn: (id: string) =>
      api.del(`/api/v1/tenants/${tenant}/tickets/${number}/links/${id}`),
    onSuccess: () => {
      invalidate();
      toast.success('Unlinked');
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'That could not be removed'),
  });

  // Insertion order per group is the order they were made, which is the order
  // the desk created the work — useful, and free.
  const groups = new Map<string, TicketLinkRow[]>();
  for (const l of links) {
    const g = groups.get(l.label) ?? [];
    g.push(l);
    groups.set(l.label, g);
  }

  return (
    <section className="rounded-md border border-line bg-surface p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wide text-ink-secondary">
          <LinkIcon aria-hidden className="h-3.5 w-3.5" />
          Related tickets
        </h2>
        <Button variant="ghost" size="sm" onClick={() => setPicking(true)}>
          <Plus aria-hidden className="h-3.5 w-3.5" />
          Link
        </Button>
        <LinkTicketDialog
          tenant={tenant}
          number={number}
          open={picking}
          onOpenChange={setPicking}
          onLinked={invalidate}
        />
      </div>

      {links.length === 0 ? (
        <p className="text-xs leading-relaxed text-ink-tertiary">
          Nothing linked. Use this when one request became several tickets — an onboarding
          split into an account and a laptop — so none of them gets closed on its own.
        </p>
      ) : (
        <div className="space-y-3">
          {[...groups.entries()].map(([label, rows]) => (
            <div key={label}>
              <p className="mb-1 text-3xs font-semibold uppercase tracking-wide text-ink-tertiary">
                {label}
              </p>
              <ul className="space-y-1">
                {rows.map((l) => (
                  <li key={l.id} className="flex items-center gap-2">
                    <Link
                      to={`/t/${l.otherTenantId}/${l.otherNumber}`}
                      className="group flex min-w-0 flex-1 items-center gap-2"
                    >
                      <span className="nums shrink-0 font-mono text-2xs text-ink-tertiary">
                        #{l.otherNumber}
                      </span>
                      <span className="truncate text-xs text-ink group-hover:underline">
                        {l.otherSubject}
                      </span>
                      {/* The state is the point: it answers "can I close
                          this yet", which is why anybody opened this panel. */}
                      <span className="shrink-0">
                        <StateBadge state={l.otherState} />
                      </span>
                    </Link>
                    {l.manual ? (
                      <button
                        type="button"
                        aria-label={`Remove link to #${l.otherNumber}`}
                        onClick={() => unlink.mutate(l.id)}
                        disabled={unlink.isPending}
                        className="shrink-0 rounded-sm p-0.5 text-ink-tertiary hover:bg-surface-sunken hover:text-ink"
                      >
                        <X aria-hidden className="h-3 w-3" />
                      </button>
                    ) : (
                      // A merge or a transfer is a record of what happened.
                      // No control at all reads better than a disabled one.
                      <span className="w-4 shrink-0" />
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * Choose the ticket to link, by searching rather than by typing it right.
 *
 * Controlled from outside: the ticket screen opens it from its own panel, and
 * the queue's context menu opens it without the reader ever having opened the
 * ticket. Both need the same picker, and two copies of it would drift.
 */
export function LinkTicketDialog({
  tenant,
  number,
  open,
  onOpenChange,
  onLinked,
}: {
  tenant: string;
  /** The ticket doing the linking. */
  number: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onLinked: () => void;
}) {
  const toast = useToast();
  const [term, setTerm] = useState('');
  const [chosen, setChosen] = useState<LookupHit | null>(null);
  const [type, setType] = useState<LinkType>('parent_of');
  const [thisIsParent, setThisIsParent] = useState(true);

  // Reopening should not resume somebody else's half-finished search.
  useEffect(() => {
    if (!open) {
      setTerm('');
      setChosen(null);
      setType('parent_of');
      setThisIsParent(true);
    }
  }, [open]);

  /**
   * Debounced, because this fires on a keystroke.
   *
   * 200ms is below the threshold where a list feels laggy and above the rate
   * at which anybody types, so a six-digit number is one query rather than
   * six. `keepPreviousData` is deliberately not used: showing the previous
   * term's results under a new term is how somebody picks the wrong ticket.
   */
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), 200);
    return () => clearTimeout(t);
  }, [term]);

  const hits = useQuery({
    queryKey: ['ticket-lookup', tenant, debounced, number],
    queryFn: () =>
      api.get<{ results: LookupHit[] }>(
        `/api/v1/tenants/${tenant}/tickets/lookup?q=${encodeURIComponent(debounced)}&exclude=${number}`,
      ),
    enabled: open && debounced.length > 0,
    staleTime: 10_000,
  });

  const link = useMutation({
    mutationFn: () =>
      api.post(`/api/v1/tenants/${tenant}/tickets/${number}/links`, {
        otherNumber: chosen!.number,
        type,
        thisIsParent,
      }),
    onSuccess: () => {
      onLinked();
      onOpenChange(false);
      toast.success(`Linked to #${chosen!.number}`);
    },
    // Verbatim: "that ticket is already part of #120198" is the sentence the
    // reader needs, and nothing here can phrase it better than the server.
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'That could not be linked'),
  });

  const results = hits.data?.results ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent width="sm" title="Link another ticket">
        <div className="space-y-3">
          <Field
            label="Find the ticket"
            required
            hint="Type a number, or part of a subject."
          >
            <Input
              autoFocus
              value={term}
              onChange={(e) => {
                setTerm(e.target.value);
                // A new search invalidates the old choice. Leaving it
                // selected is how the wrong ticket gets linked.
                setChosen(null);
              }}
              placeholder="120198 or laptop"
            />
          </Field>

          {/* THE SUBJECT IS THE POINT. A number is unconfirmable on its own —
              120203 and 120230 both exist — so nothing can be picked without
              seeing what it actually is. */}
          {term.trim().length > 0 && !chosen && (
            <div className="max-h-56 overflow-y-auto rounded-md border border-line-subtle">
              {hits.isLoading ? (
                <p className="px-2.5 py-2 text-2xs text-ink-tertiary">Looking…</p>
              ) : results.length === 0 ? (
                <p className="px-2.5 py-2 text-2xs text-ink-tertiary">
                  Nothing matches that.
                </p>
              ) : (
                <ul className="divide-y divide-line-subtle">
                  {results.map((r) => (
                    <li key={r.number}>
                      <button
                        type="button"
                        onClick={() => setChosen(r)}
                        className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left hover:bg-surface-sunken"
                      >
                        <span className="nums shrink-0 font-mono text-2xs text-ink-tertiary">
                          #{r.number}
                        </span>
                        <span className="truncate text-xs text-ink">{r.subject}</span>
                        <span className="ml-auto shrink-0">
                          <StateBadge state={r.state} />
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {chosen && (
            <div className="flex items-center gap-2 rounded-md border border-brand-line bg-brand-tint/30 px-2.5 py-2">
              <span className="nums shrink-0 font-mono text-2xs text-ink-tertiary">
                #{chosen.number}
              </span>
              <span className="truncate text-xs text-ink">{chosen.subject}</span>
              <button
                type="button"
                aria-label="Choose a different ticket"
                onClick={() => setChosen(null)}
                className="ml-auto shrink-0 rounded-sm p-0.5 text-ink-tertiary hover:text-ink"
              >
                <X aria-hidden className="h-3 w-3" />
              </button>
            </div>
          )}

          <Field label="Relationship" hint={LINK_KINDS[type].hint}>
            <Select
              value={type}
              options={MANUAL_LINK_TYPES.map((t) => ({
                value: t,
                label: LINK_KINDS[t].forward,
              }))}
              onValueChange={(v) => setType(v as LinkType)}
            />
          </Field>

          {type === 'parent_of' && (
            <Field label="Which is the request?">
              <Select
                value={thisIsParent ? 'this' : 'other'}
                options={[
                  { value: 'this', label: `#${number} is the request` },
                  {
                    value: 'other',
                    label: chosen ? `#${chosen.number} is the request` : 'The other one is',
                  },
                ]}
                onValueChange={(v) => setThisIsParent(v === 'this')}
              />
            </Field>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={link.isPending}
            // Nothing can be linked until a row has been CHOSEN. A typed
            // number that was never confirmed is the mistake this prevents.
            disabled={!chosen || link.isPending}
            onClick={() => link.mutate()}
          >
            Link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
